// Currency metadata. The scale is the number of minor-unit digits (ISO 4217 "minor unit").
// Only currencies listed here are accepted; adding one is a deliberate change with a test.

export type CurrencyInfo = {
  readonly code: string;
  readonly scale: number;
  /**
   * The locale of the currency's own country: where its sign stands there ("£" before the
   * number in the UK, "zł" after it in Poland). None: the UI locale decides (the euro has no
   * single home; the Gulf dinars are written in Arabic).
   */
  readonly home?: string;
};

const CURRENCIES: Readonly<Record<string, CurrencyInfo>> = Object.freeze({
  GBP: { code: 'GBP', scale: 2, home: 'en-GB' },
  PLN: { code: 'PLN', scale: 2, home: 'pl-PL' },
  EUR: { code: 'EUR', scale: 2 },
  USD: { code: 'USD', scale: 2, home: 'en-US' },
  CHF: { code: 'CHF', scale: 2, home: 'de-CH' },
  CZK: { code: 'CZK', scale: 2, home: 'cs-CZ' },
  SEK: { code: 'SEK', scale: 2, home: 'sv-SE' },
  NOK: { code: 'NOK', scale: 2, home: 'nb-NO' },
  DKK: { code: 'DKK', scale: 2, home: 'da-DK' },
  HUF: { code: 'HUF', scale: 2, home: 'hu-HU' },
  CAD: { code: 'CAD', scale: 2, home: 'en-CA' },
  AUD: { code: 'AUD', scale: 2, home: 'en-AU' },
  JPY: { code: 'JPY', scale: 0, home: 'ja-JP' },
  KWD: { code: 'KWD', scale: 3 },
  BHD: { code: 'BHD', scale: 3 },
});

export const SUPPORTED_CURRENCIES: readonly string[] = Object.freeze(Object.keys(CURRENCIES));

export class UnsupportedCurrencyError extends Error {
  readonly currency: string;
  constructor(currency: string) {
    super(`Unsupported currency: ${currency}`);
    this.name = 'UnsupportedCurrencyError';
    this.currency = currency;
  }
}

export function isSupportedCurrency(code: string): boolean {
  return Object.hasOwn(CURRENCIES, code);
}

export function currencyInfo(code: string): CurrencyInfo {
  const info = Object.hasOwn(CURRENCIES, code) ? CURRENCIES[code] : undefined;
  if (!info) throw new UnsupportedCurrencyError(code);
  return info;
}

export function currencyScale(code: string): number {
  return currencyInfo(code).scale;
}

/** 10^scale as bigint, e.g. 100n for GBP. */
export function minorFactor(code: string): bigint {
  return 10n ** BigInt(currencyScale(code));
}
